import React from "react";
import { CheckCircle2, XCircle } from "lucide-react";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDateTime } from "../../../utils/datetime";

/**
 * Where the numbers on this page come from, and how fresh they are.
 *
 * A TIMESTAMP IS SHOWN ONLY WHERE ONE EXISTS. The reference design reads
 * "Aktualisiert: vor 2 Min." beside every source. For Bexio that is real —
 * `/api/admin/bexio/status` returns `last_successful_sync.finished_at`. For Vantio's
 * own tenancy data there is no sync at all: it is written directly, so there is
 * nothing to be fresh or stale relative to. Printing a relative time there would be
 * inventing one, so that row states availability and stops.
 *
 * Connection state is icon + word, never colour alone.
 */

/**
 * The connection's state, read from the fields the contract actually has.
 *
 * `BexioConnectionStatus` carries `configured`, `status` and `last_error_category` —
 * there is no `is_connected` boolean to lean on. `configured` means a connection row
 * exists; `status === "error"` means the last sync failed. Both matter: a configured
 * connection that is erroring is not "Verbunden", and saying so would present a
 * broken pipe as a healthy one.
 */
function connectionState(connection) {
  if (!connection?.configured) return { ok: false, label: "Nicht verbunden" };
  if (connection.status === "error") return { ok: false, label: "Sync-Fehler" };
  return { ok: true, label: "Verbunden" };
}

function StatusRow({ label, state, detail }) {
  return (
    <li className="flex items-start justify-between gap-3 py-2 first:pt-0 last:pb-0">
      <div className="min-w-0">
        <p className="text-[12px] font-medium text-card-foreground">{label}</p>
        {detail ? (
          <p className="truncate text-[11px] text-muted-foreground">{detail}</p>
        ) : null}
      </div>
      <span
        className={`inline-flex shrink-0 items-center gap-1 text-[11px] font-medium ${
          state.ok ? "text-emerald-700 dark:text-emerald-400" : "text-muted-foreground"
        }`}
      >
        {state.ok ? (
          <CheckCircle2 aria-hidden className="h-3.5 w-3.5" />
        ) : (
          <XCircle aria-hidden className="h-3.5 w-3.5" />
        )}
        {state.label}
      </span>
    </li>
  );
}

export default function OverviewDataSourceCard({ bexioStatus, loading }) {
  const status = bexioStatus?.data;
  const error = bexioStatus?.error;
  const bexioState = connectionState(status?.connection);
  const finishedAt = status?.last_successful_sync?.finished_at;

  return (
    <section
      aria-labelledby="overview-sources-heading"
      aria-busy={loading || undefined}
      className="rounded-xl border border-border bg-card p-4 shadow-sm"
    >
      <h2
        id="overview-sources-heading"
        className="text-[13px] font-semibold text-card-foreground"
      >
        Datenquellen-Status
      </h2>

      <div className="mt-3">
        {loading ? (
          <div className="space-y-2">
            <Skeleton className="h-8 w-full" />
            <Skeleton className="h-8 w-full" />
          </div>
        ) : (
          <ul className="divide-y divide-border">
            {error ? (
              <li className="py-2 first:pt-0">
                <p role="alert" className="text-[12px] text-destructive">
                  bexio-Status konnte nicht geladen werden.
                </p>
              </li>
            ) : (
              <StatusRow
                label="bexio"
                state={bexioState}
                detail={
                  finishedAt
                    ? `Letzter Sync: ${formatDateTime(finishedAt)}`
                    : "Noch kein erfolgreicher Sync"
                }
              />
            )}
            <StatusRow
              label="Mietverhältnisse / Vertragsdaten"
              state={{ ok: true, label: "Aktiv" }}
              detail="Direkt in Vantio erfasst"
            />
          </ul>
        )}
      </div>
    </section>
  );
}
